import React, { useCallback, useEffect, useState } from "react";
import { Button, Input, Modal, Spinner } from "reactstrap";
import { useTranslation } from "react-i18next";
import { ErrorToaster, successToaster } from "components/common/toast";
import CustomPagination from "components/common/Pagination";
import { isFoodCard, kindT } from "./cardKind";
import handleError from "utils/handleError";
import {
  AssignProductsAPI,
  GetCardProductsAPI,
  GetMenuCardAPI,
} from "network/menuCards";

const PER_PAGE = 10;

// Dalla carta annidata (sezioni > categorie > sottocategorie > prodotti) ricava i prodotti gia' assegnati.
const assignedFrom = (card) => {
  const items = [];
  (card?.sections || []).forEach((s) =>
    (s.categories || []).forEach((c) =>
      (c.sub_categories || []).forEach((sc) =>
        (sc.products || []).forEach((p) => items.push({ uuid: p.uuid, name: p.name }))
      )
    )
  );
  return items;
};

const AssignWinesModal = ({ card, open, onClose, onSaved }) => {
  const { t: baseT, i18n } = useTranslation("cards");
  // Food Card: si scelgono i piatti di Food Product; Drinks Card: i vini con scheda Wine Data.
  const food = isFoodCard(card);
  const t = kindT(baseT, i18n, food);
  const [products, setProducts] = useState([]);
  const [selected, setSelected] = useState({}); // uuid -> nome, resta valido cambiando pagina
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // All'apertura: carica i vini gia' in carta.
  useEffect(() => {
    if (!open || !card) return;
    setSearch("");
    setPage(1);
    setSelected({});
    GetMenuCardAPI(card.uuid, true)
      .then((res) => {
        const map = {};
        assignedFrom(res?.data).forEach((p) => (map[p.uuid] = p.name));
        setSelected(map);
      })
      .catch((e) => ErrorToaster(handleError(e?.status_code, e?.message)));
  }, [open, card]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await GetCardProductsAPI({ page, perPage: PER_PAGE, query: search, kind: food ? "food" : "drinks" });
      setProducts(res?.data?.products || []);
      setTotalPages(res?.data?.total_pages || 1);
    } catch (e) {
      ErrorToaster(handleError(e?.status_code, e?.message));
    } finally {
      setLoading(false);
    }
  }, [page, search, food]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const toggle = (p) =>
    setSelected((prev) => {
      const next = { ...prev };
      if (next[p.uuid]) delete next[p.uuid];
      else next[p.uuid] = p.name;
      return next;
    });

  const count = Object.keys(selected).length;

  const save = async () => {
    if (count === 0) {
      ErrorToaster(t("assign.selectOne"));
      return;
    }
    setSaving(true);
    try {
      await AssignProductsAPI(card.uuid, Object.keys(selected));
      successToaster(t("assign.updated"));
      onSaved?.();
      onClose();
    } catch (e) {
      ErrorToaster(handleError(e?.status_code, e?.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={open} toggle={onClose} className="modal-lg">
      <div className="modal-header">
        <h5 className="modal-title">{t(food ? "assign.titleDishes" : "assign.titleWines", { name: card?.name })}</h5>
        <button aria-label={t("close")} className="close" type="button" onClick={onClose}>
          <span aria-hidden="true">×</span>
        </button>
      </div>
      <div className="modal-body">
        <p className="text-muted small">
          {t("assign.intro")}
        </p>
        <Input
          placeholder={t("assign.search")}
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          className="mb-3"
        />
        {loading ? (
          <div className="text-center py-4"><Spinner size="sm" /></div>
        ) : products.length === 0 ? (
          <p className="text-center text-muted py-4">{t("assign.none")}</p>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th style={{ width: 40 }}></th>
                <th>{t("assign.wine")}</th>
                <th className="text-right">{t("assign.price")}</th>
                {!food && <th className="text-right">{t("assign.stock")}</th>}
              </tr>
            </thead>
            <tbody>
              {products.map((p) => (
                <tr key={p.uuid} style={{ cursor: "pointer" }} onClick={() => toggle(p)}>
                  <td>
                    <input
                      type="checkbox"
                      checked={!!selected[p.uuid]}
                      onChange={() => toggle(p)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </td>
                  <td>
                    {p.name}
                    {p.is_available === false && <span className="badge badge-warning ml-2">{t("assign.soldOut")}</span>}
                    {food && p.description && <small className="d-block text-muted">{p.description}</small>}
                  </td>
                  <td className="text-right">{p.price != null && p.price !== "" ? `€ ${p.price}` : "-"}</td>
                  {!food && <td className="text-right">{p.stock ?? "-"}</td>}
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <CustomPagination currentPage={page} totalPages={totalPages} onPageChange={setPage} isLoading={loading} />
      </div>
      <div className="modal-footer d-flex justify-content-between">
        <span className="text-muted">{t("assign.selected", { count })}</span>
        <div>
          <Button color="secondary" onClick={onClose} disabled={saving}>{t("cancel")}</Button>
          <Button color="primary" onClick={save} disabled={saving}>
            {saving ? t("saving") : t("assign.save")}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default AssignWinesModal;
